import React, { useState, useEffect, useId, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue, dataLocationToPath } from '@ea-lab/reactive-json';
import { getTagColorClass } from '../../utils/tagColors';
import { getAllowedTargets } from './kanbanTransitionPolicy';
import ConfirmTrashModal from '../shared/ui/ConfirmTrashModal';
import { PriorityDropdown } from '../shared/fields/priority-icon';
import { ComplexityDots } from '../shared/fields/complexity-dots';
import { initials } from '../shared/ui/InitialsAvatar';

// Entity-reference fields (tags, assignee) come back as scalar ids from the
// collection display (decoupled_card) but as nested objects ({id,name}) from the
// save/decoupled response that DataSync writes back into the card store. Normalize
// to the scalar id so tag labels and avatars resolve consistently in both shapes.
const refId = (v) => (v && typeof v === 'object') ? (v.id ?? v.target_id ?? v.value ?? v.tid ?? '') : v;

// ── KanbanTypeBadge ─────────────────────────────────────────────────────────
// Renders the task-type pill with colour coding.
// Green  = type that has subtypes (parentId === 0 and at least one child exists).
// Yellow = subtype (parentId > 0).
// Blue   = flat type (parentId === 0, no children).
export const KanbanTypeBadge = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const typeTid = evaluateTemplateValue({ valueToEvaluate: props.taskType, globalDataContext, templateContext });
  const typeOptions = evaluateTemplateValue({ valueToEvaluate: props.typeOptions, globalDataContext, templateContext }) || [];

  const typeOption = typeOptions.find(o => String(o.value) === String(typeTid));
  const label = typeOption?.label || String(typeTid || '');
  const parentId = Number(typeOption?.data?.parentId ?? 0);

  const isSubType = parentId > 0;
  const hasSubTypes = !isSubType && typeOptions.some(o => Number(o.data?.parentId) === Number(typeTid));

  const cls = hasSubTypes
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : isSubType
    ? 'bg-amber-100 text-amber-800 border-amber-200'
    : 'bg-sky-100 text-sky-700 border-sky-200';

  return (
    <span
      className={`inline-block max-w-[8.5rem] truncate align-middle text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${cls}`}
      title={label}
    >
      {label}
    </span>
  );
};

// ── KanbanTaskLink ───────────────────────────────────────────────────────────
// Le titre de la tâche en lien vers la fiche, plus UN panneau au survol qui
// porte les deux choses que la carte tronquée ne peut pas montrer :
//   - le titre COMPLET, mais seulement quand le clamp sur 2 lignes le coupe ;
//   - la description de la tâche, quand il y en a une.
// Rendu en portail comme KanbanDescriptionEye, pour que le débordement de la
// colonne ne le rogne jamais. a11y (WCAG 2.2 §1.4.13) : il s'ouvre aussi au
// focus clavier, reste ouvert quand le pointeur passe dessus, et Échap ferme.
const HOVER_OPEN_DELAY = 250;
const HOVER_CLOSE_DELAY = 150;
const HOVER_PANEL_WIDTH = 320;

export const KanbanTaskLink = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const taskId = evaluateTemplateValue({ valueToEvaluate: props.taskId, globalDataContext, templateContext });
  const title = evaluateTemplateValue({ valueToEvaluate: props.title, globalDataContext, templateContext });
  const rawDescription = evaluateTemplateValue({ valueToEvaluate: props.description, globalDataContext, templateContext }) || '';
  // Même normalisation que l'œil « aperçu » : le champ est du texte riche, le
  // panneau affiche du texte brut (jamais dangerouslySetInnerHTML sur du contenu
  // saisi par un utilisateur).
  const description = String(rawDescription).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

  const panelId = useId();
  const linkRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [coords, setCoords] = useState(null);

  // Le clamp (`line-clamp-2`) vit sur un CONTENEUR déclaré dans KanbanCard.yaml,
  // pas sur ce <a> en ligne : le débordement ne se mesure que là — et ce
  // conteneur n'est pas forcément le parent direct (reactive-json est libre
  // d'imbriquer). On remonte donc jusqu'au premier ancêtre qui rogne.
  useEffect(() => {
    const findClipper = (node) => {
      let el = node?.parentElement;
      for (let i = 0; el && i < 4; i += 1, el = el.parentElement) {
        const style = window.getComputedStyle(el);
        const clamped = style.webkitLineClamp && style.webkitLineClamp !== 'none';
        if (clamped || style.overflow === 'hidden' || style.overflowY === 'hidden' || style.textOverflow === 'ellipsis') return el;
      }
      return node?.parentElement ?? null;
    };
    const el = findClipper(linkRef.current);
    if (!el) return undefined;
    const measure = () => setIsTruncated(
      el.scrollHeight - el.clientHeight > 1 || el.scrollWidth - el.clientWidth > 1,
    );
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Une colonne plus étroite coupe un titre qui tenait avant : on re-mesure.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [title]);

  const hasPanel = !!((isTruncated && title) || description);

  const cancelTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };
  useEffect(() => cancelTimers, []);

  const open = () => {
    if (!hasPanel) return;
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => {
      const el = linkRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - HOVER_PANEL_WIDTH - 8));
      // Bascule au-dessus du titre quand la carte est basse dans la fenêtre.
      const flipUp = window.innerHeight - r.bottom < 180 && r.top > 180;
      setCoords(flipUp
        ? { bottom: window.innerHeight - r.top + 6, left }
        : { top: r.bottom + 6, left });
    }, HOVER_OPEN_DELAY);
  };

  const close = (immediate = false) => {
    cancelTimers();
    if (immediate) { setCoords(null); return; }
    closeTimer.current = setTimeout(() => setCoords(null), HOVER_CLOSE_DELAY);
  };

  // Fermeture immédiate (Échap, scroll). Défini DANS l'effet, sur des refs
  // stables : pas de dépendance sur `close`, donc pas de réabonnement à chaque
  // rendu de la carte.
  useEffect(() => {
    if (!coords) return undefined;
    const dismiss = () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
      setCoords(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [coords]);

  return (
    <>
      <a
        ref={linkRef}
        href={`/task-edit?id=${taskId}`}
        className="hover:text-iris-600 hover:underline cursor-pointer"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={open}
        onMouseLeave={() => close()}
        onFocus={open}
        onBlur={() => close(true)}
        aria-describedby={coords ? panelId : undefined}
      >
        {title}
      </a>
      {coords && createPortal(
        <div
          id={panelId}
          role="tooltip"
          style={{ position: 'fixed', ...coords, width: HOVER_PANEL_WIDTH, zIndex: 9999 }}
          className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-700 leading-relaxed max-h-60 overflow-y-auto"
          onMouseEnter={() => clearTimeout(closeTimer.current)}
          onMouseLeave={() => close()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {isTruncated && title && (
            <p className="font-semibold text-slate-800 break-words">{title}</p>
          )}
          {isTruncated && title && description && <hr className="my-2 border-slate-100" />}
          {description && <p className="break-words">{description}</p>}
        </div>,
        document.body,
      )}
    </>
  );
};

// ── KanbanSubtaskButton ────────────────────────────────────────────────────
// Shown only for task types that have at least one subtype defined in the
// backend taxonomy (i.e. another term in typeOptions has parentId === this TID).
// Dispatches a CustomEvent that KanbanBoard listens to for drill-down.
export const KanbanSubtaskButton = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const taskTypeTid = evaluateTemplateValue({ valueToEvaluate: props.taskType, globalDataContext, templateContext });
  const typeOptions = evaluateTemplateValue({ valueToEvaluate: props.typeOptions, globalDataContext, templateContext }) || [];
  const taskId = evaluateTemplateValue({ valueToEvaluate: props.taskId, globalDataContext, templateContext });
  const subtaskCount = evaluateTemplateValue({ valueToEvaluate: props.subtaskCount, globalDataContext, templateContext }) || 0;

  const hasSubTypes = typeOptions.some(o => Number(o.data?.parentId) === Number(taskTypeTid));
  if (!hasSubTypes) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    document.dispatchEvent(new CustomEvent('kanbanSubtaskDrilldown', { detail: { taskId } }));
  };

  return (
    <button
      onPointerDown={(e) => e.stopPropagation()}
      onClick={handleClick}
      className="flex-shrink-0 inline-flex items-center gap-1 text-xs bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-iris-600 px-1.5 py-1 rounded transition-colors border border-transparent hover:border-iris-200 whitespace-nowrap"
      title={`${subtaskCount} sous-tâche${subtaskCount > 1 ? 's' : ''} — cliquer pour les voir`}
      aria-label={`${subtaskCount} sous-tâche${subtaskCount > 1 ? 's' : ''}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5 5-5-5M12 1.5V13"/>
      </svg>
      <span className="font-semibold tabular-nums">{subtaskCount}</span>
    </button>
  );
};

// ── KanbanMoveToTopButton ────────────────────────────────────────────────────
// Arrow-up button, revealed on card hover: sends the card to the top of its
// column. Dispatches on the shared kanban* bus and nothing else — KanbanBoard
// owns the rank computation, the optimistic move, the persistence and the 10s
// landing ring, exactly as for a drop. A second way to reorder would be a second
// way to be wrong.
export const KanbanMoveToTopButton = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const taskId = evaluateTemplateValue({ valueToEvaluate: props.taskId, globalDataContext, templateContext });
  const projectArchived = evaluateTemplateValue({ valueToEvaluate: props.projectArchived, globalDataContext, templateContext });

  // An archived project is read-only: don't offer the action at all rather than
  // refuse it on click. (The YAML handles the archived-TASK case, which the card
  // knows about on its own.)
  if (projectArchived) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    document.dispatchEvent(new CustomEvent('kanbanMoveToTop', { detail: { taskId } }));
  };

  return (
    <button
      // The whole card is a drag handle: without stopping the pointer here, the
      // click would start a drag instead (same guard as the other card buttons).
      onPointerDown={(e) => e.stopPropagation()}
      onClick={handleClick}
      // Secondary action: revealed by the card's `group` hover. focus-visible
      // keeps it reachable by keyboard, which has no hover.
      className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 flex-shrink-0 inline-flex items-center text-slate-400 hover:text-iris-600 hover:bg-slate-100 p-1 rounded transition-colors"
      title="Remonter en tête de colonne"
      aria-label="Remonter cette carte en tête de colonne"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
      </svg>
    </button>
  );
};

// ── KanbanDeleteButton ───────────────────────────────────────────────────────
// Trash icon button (visible on hover). Dispatches a CustomEvent for deletion.
// KanbanBoard handles the actual DELETE request so it can update its local state.
export const KanbanDeleteButton = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const deleteUrl = evaluateTemplateValue({ valueToEvaluate: props.deleteUrl, globalDataContext, templateContext });
  const taskTitle = evaluateTemplateValue({ valueToEvaluate: props.taskTitle, globalDataContext, templateContext });
  const [showConfirm, setShowConfirm] = useState(false);

  if (!deleteUrl) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    document.dispatchEvent(new CustomEvent('kanbanDeleteTask', { detail: { deleteUrl } }));
    setShowConfirm(false);
  };

  return (
    <>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={handleClick}
        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-500 rounded hover:bg-rose-50 transition-all"
        title="Supprimer"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
      </button>
      {showConfirm && (
        <ConfirmTrashModal
          isOpen
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
          title={`Supprimer « ${taskTitle || 'cette tâche'} » ?`}
          description="Cette tâche sera déplacée dans la corbeille. Vous pourrez la restaurer pendant 90 jours."
          confirmLabel="Déplacer dans la corbeille"
        />
      )}
    </>
  );
};

// ── KanbanArchiveButton ──────────────────────────────────────────────────────
// Archive icon button (visible on hover). Dispatches a CustomEvent for archiving.
export const KanbanArchiveButton = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const taskId = evaluateTemplateValue({ valueToEvaluate: props.taskId, globalDataContext, templateContext });
  const taskTitle = evaluateTemplateValue({ valueToEvaluate: props.taskTitle, globalDataContext, templateContext });
  const [showConfirm, setShowConfirm] = useState(false);

  if (!taskId) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    document.dispatchEvent(new CustomEvent('kanbanArchiveTask', { detail: { taskId } }));
    setShowConfirm(false);
  };

  return (
    <>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={handleClick}
        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-sky-500 rounded hover:bg-sky-50 transition-all"
        title="Archiver"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="5" x="2" y="3" rx="1"/>
          <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/>
          <path d="M10 12h4"/>
        </svg>
      </button>
      {showConfirm && (
        <ConfirmTrashModal
          isOpen
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
          title={`Archiver « ${taskTitle || 'cette tâche'} » ?`}
          description="Cette tâche sera archivée en lecture seule. Vous pourrez la désarchiver à tout moment."
          confirmLabel="Archiver"
          variant="archive"
        />
      )}
    </>
  );
};

// ── KanbanUnarchiveButton ─────────────────────────────────────────────────────
// Désarchiver an archived task. Rendered only for admins / project leads
// (canUnarchive); the card YAML additionally only shows it on archived cards.
// The server re-checks the permission (a 403 surfaces as a French alert in
// KanbanBoard's kanbanUnarchiveTask handler).
export const KanbanUnarchiveButton = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const taskId = evaluateTemplateValue({ valueToEvaluate: props.taskId, globalDataContext, templateContext });
  const canUnarchive = evaluateTemplateValue({ valueToEvaluate: props.canUnarchive, globalDataContext, templateContext });
  const [showConfirm, setShowConfirm] = useState(false);

  if (!taskId || !canUnarchive) return null;

  const handleConfirm = () => {
    document.dispatchEvent(new CustomEvent('kanbanUnarchiveTask', { detail: { taskId } }));
    setShowConfirm(false);
  };

  return (
    <>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
        className="p-1 text-amber-500 hover:text-emerald-600 rounded hover:bg-emerald-50 transition-all"
        title="Désarchiver"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7v6h6" />
          <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
        </svg>
      </button>
      {showConfirm && (
        <ConfirmTrashModal
          isOpen
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
          title="Désarchiver cette tâche ?"
          description="La tâche (et ses sous-tâches archivées avec elle) redeviendra modifiable."
          confirmLabel="Désarchiver"
          variant="archive"
        />
      )}
    </>
  );
};

// ── KanbanPriorityIcon ────────────────────────────────────────────────────────
// CDC-TMA T12: Jira-style priority glyph (1 Lowest … 5 Highest) replacing the
// stars on the card. Click opens a dropdown of the 5 levels (icon + label),
// persisted into apiData by DataSync onIdle.
export const KanbanPriorityIcon = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const value = Number(evaluateTemplateValue({ valueToEvaluate: props.value, globalDataContext, templateContext })) || 0;
  const updateField = props.updateField; // e.g. "data.priority"
  // `dataLocation` est la forme reactive-json ordinaire (`~.data.priority`) :
  // elle est résolue en chemin ABSOLU, donc utilisable dans une boucle où chaque
  // ligne a le sien. `updateField` reste le mode historique de la carte Kanban,
  // qui vit dans un subroot où le syncable est toujours à `apiData`.
  const dataLocation = props.dataLocation;

  const handleChange = (level) => {
    if (!globalDataContext?.updateData) return;
    if (dataLocation) {
      globalDataContext.updateData(level, dataLocationToPath({
        dataLocation,
        currentPath: templateContext?.templatePath,
        globalDataContext,
        templateContext,
      }));
      return;
    }
    if (!updateField) return;
    globalDataContext.updateData(level, `apiData.${updateField}`);
  };

  return <PriorityDropdown value={value} onChange={handleChange} size={18} />;
};

// ── KanbanComplexityDots ──────────────────────────────────────────────────────
// CDC-TMA T11: 5 coloured dots showing the task complexity (1-5), from green to
// red. Click a dot to set the complexity inline (apiData write, persisted by
// DataSync onIdle). Visual is the shared ComplexityDots component.
export const KanbanComplexityDots = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const value = evaluateTemplateValue({ valueToEvaluate: props.value, globalDataContext, templateContext }) || 0;
  const updateField = props.updateField; // e.g. "data.complexity"

  const handleChange = (level) => {
    if (!updateField || !globalDataContext?.updateData) return;
    globalDataContext.updateData(level, `apiData.${updateField}`);
  };

  return <ComplexityDots value={value} onChange={handleChange} />;
};

// ── KanbanDescriptionEye ──────────────────────────────────────────────────────
// CDC-TMA T17: an eye icon that opens a popover preview of the task description
// (read-only) without opening the ticket. Hidden when the task has no
// description. The popover is portal-rendered so it is never clipped.
export const KanbanDescriptionEye = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const html = evaluateTemplateValue({ valueToEvaluate: props.value, globalDataContext, templateContext }) || '';
  const text = String(html).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // a11y (WCAG 2.1.1/2.4.3): Échap ferme et rend le focus au déclencheur.
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    window.addEventListener('scroll', close, true);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // a11y: the panel is portaled to <body> (out of DOM order), so move focus into
  // it on open — otherwise a keyboard user could never reach the items.
  useEffect(() => {
    if (open && popRef.current) {
      popRef.current.querySelector('button')?.focus();
    }
  }, [open, coords]);

  if (!text) return null;

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: Math.max(8, r.left - 120) });
    }
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        onPointerDown={(e) => e.stopPropagation()}
        title="Aperçu de la description"
        aria-label="Aperçu de la description"
        className="p-1 text-slate-400 hover:text-iris-600 rounded hover:bg-iris-50 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999, maxWidth: 320 }}
          className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-700 leading-relaxed max-h-60 overflow-y-auto"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
};

// ── KanbanTransitionMenu ─────────────────────────────────────────────────────
// T17 (CDC-TMA #53): a menu on the card listing the workflow transitions the
// task may ACTUALLY move to — i.e. reachable by the type's workflow AND
// permitted for the user's project roles AND not blocked by open subtasks. The
// list is produced by the single policy source (getAllowedTargets), so it can
// never diverge from the drag-and-drop guard. Picking one dispatches a
// `kanbanTransitionRequest` document event; KanbanBoard applies + persists it
// (the server still enforces role/subtask guards). Useful in list/mobile where
// drag is awkward. Renders nothing when no target is allowed (terminal state,
// type without a workflow, role/subtask-blocked).
export const KanbanTransitionMenu = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const ev = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });

  const taskId = ev(props.taskId);
  const typeId = ev(props.taskType);
  const currentState = ev(props.currentState);

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', close, true);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  // Allowed target states (reachable ∩ role-permitted ∩ subtasks-OK), resolved
  // from the module-level policy the board seeds via setPolicyContext. We pass
  // the task in policy shape ({ data: { id, type, state } }); the policy reads
  // parentId from its own _tasks context to evaluate the subtask guard, so the
  // menu offers exactly what a drop would accept — no divergence.
  const targets = getAllowedTargets({ data: { id: taskId, type: typeId, state: currentState } });

  if (!taskId || targets.length === 0) return null;

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: Math.max(8, r.left - 80) });
    }
    setOpen((o) => !o);
  };

  const choose = (toState) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('kanbanTransitionRequest', { detail: { taskId, toState } }));
    setOpen(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        onPointerDown={(e) => e.stopPropagation()}
        title="Changer le statut"
        aria-label="Changer le statut"
        aria-haspopup="true"
        aria-expanded={open}
        className="p-1 text-slate-400 hover:text-iris-600 rounded hover:bg-iris-50 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3 4 7l4 4" /><path d="M4 7h16" /><path d="m16 21 4-4-4-4" /><path d="M20 17H4" />
        </svg>
      </button>
      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999, minWidth: 160 }}
          className="bg-white border border-slate-200 rounded-lg shadow-lg py-1 text-xs"
          onPointerDown={(e) => e.stopPropagation()}
          role="group"
          aria-label="Changer le statut"
        >
          <div className="px-3 py-1 text-[10px] uppercase font-semibold text-slate-400">Déplacer vers</div>
          {targets.map((t) => (
            <button
              key={t.to}
              type="button"
              onClick={choose(t.to)}
              className="block w-full text-left px-3 py-1.5 text-slate-700 hover:bg-iris-50 hover:text-iris-700"
            >
              {t.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

// ── KanbanTagList ────────────────────────────────────────────────────────────
// Interactive tag list. Writes updated tags array back into ~~.apiData.
export const KanbanTagList = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const wrapperRef = useRef(null);

  const rawTags = evaluateTemplateValue({ valueToEvaluate: props.value, globalDataContext, templateContext }) || [];
  const tags = Array.isArray(rawTags) ? rawTags.map(refId) : [];
  const tagOptions = evaluateTemplateValue({ valueToEvaluate: props.tagOptions, globalDataContext, templateContext }) || [];
  const updateField = props.updateField; // field name inside apiData, e.g. "tags"

  const resolveTagLabel = (tid) => {
    const opt = tagOptions.find(o => String(o.value) === String(tid));
    return opt ? opt.label : String(tid);
  };

  const resolveTagTid = (name) => {
    const opt = tagOptions.find(o => o.label === name);
    return opt ? opt.value : name;
  };

  const availableTagNames = tagOptions.map(o => o.label).filter(Boolean);
  const filteredTagNames = availableTagNames.filter(name => {
    const matches = name.toLowerCase().includes(inputValue.toLowerCase());
    const alreadyAdded = tags.some(tid => String(tid) === String(resolveTagTid(name)));
    return matches && !alreadyAdded;
  });

  useEffect(() => {
    if (!isAddingTag) return;
    const onOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsAddingTag(false);
      }
    };
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [isAddingTag]);

  const writeTags = (newTags) => {
    if (!updateField || !globalDataContext?.updateData) return;
    // Same apiData write pattern as KanbanPriorityIcon: write into the nested path so the
    // store bubbles the notification up to DataSync's "apiData" subscription.
    globalDataContext.updateData(newTags, `apiData.${updateField}`);
  };

  const handleRemoveTag = (idx) => {
    writeTags(tags.filter((_, i) => i !== idx));
  };

  const handleAddTag = (name) => {
    const tid = resolveTagTid(name);
    if (tid && !tags.some(t => String(t) === String(tid))) {
      writeTags([...tags, tid]);
    }
    setIsAddingTag(false);
    setInputValue('');
  };


  return (
    <div className="flex flex-wrap gap-1 mb-1 relative" onPointerDown={(e) => e.stopPropagation()}>
      {tags.map((tid, idx) => {
        const label = resolveTagLabel(tid);
        return (
          <span
            key={idx}
            className={`group/tag relative px-2 py-0.5 rounded text-[10px] font-medium pr-4 ${getTagColorClass(label)}`}
          >
            {label}
            <button
              onClick={(e) => { e.stopPropagation(); handleRemoveTag(idx); }}
              className="absolute right-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover/tag:opacity-100 p-0.5 hover:bg-black/10 rounded-full transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </span>
        );
      })}

      {isAddingTag ? (
        <div ref={wrapperRef} className="relative">
          <input
            autoFocus
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-24 px-1 py-0.5 text-[10px] border rounded shadow-sm focus:ring-1 focus:ring-iris-500 focus:border-iris-500 outline-none"
            placeholder="Tag…"
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleAddTag(inputValue.trim()); }
              else if (e.key === 'Escape') setIsAddingTag(false);
            }}
            onClick={(e) => e.stopPropagation()}
          />
          {filteredTagNames.length > 0 && (
            <div className="absolute top-full left-0 mt-1 w-32 bg-white border border-slate-200 rounded shadow-lg z-50 max-h-32 overflow-y-auto">
              {filteredTagNames.map(tag => (
                <div
                  key={tag}
                  className="px-2 py-1 text-[10px] hover:bg-iris-50 cursor-pointer text-slate-700"
                  onClick={(e) => { e.stopPropagation(); handleAddTag(tag); }}
                >
                  {tag}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); setIsAddingTag(true); setInputValue(''); }}
          className="px-2 py-0.5 rounded text-[10px] bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-iris-600 border border-dashed border-slate-300 transition-colors"
          title="Ajouter un tag"
        >
          +
        </button>
      )}
    </div>
  );
};

// ── KanbanUserAvatar ─────────────────────────────────────────────────────────
export const KanbanUserAvatar = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const assignee = refId(evaluateTemplateValue({ valueToEvaluate: props.assignee, globalDataContext, templateContext }));
  const users = evaluateTemplateValue({ valueToEvaluate: props.users, globalDataContext, templateContext }) || [];
  const size = props.size || 'sm';

  if (!assignee) return null;

  const avatarSize = size === 'sm' ? 'avatar-xs text-[8px]' : 'text-xs';
  const user = users.find(u => String(u.id) === String(assignee) || u.name === assignee);
  const displayName = user?.name || String(assignee);

  return (
    <span
      className={`avatar ${avatarSize}`}
      title={displayName}
    >
      {initials(displayName)}
    </span>
  );
};

// ── KanbanSyncBridge ─────────────────────────────────────────────────────────
// Invisible component that watches apiData.data fields in the subroot store.
// When a value changes (e.g. user clicks a star), it dispatches a
// "kanbanCardUpdated" CustomEvent so KanbanBoard can update its own tasks
// state immediately — no waiting for the DataSync round-trip.  Persistence
// is still handled by the DataSync component in KanbanCard.yaml.
export const KanbanSyncBridge = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const taskId = evaluateTemplateValue({ valueToEvaluate: props.taskId, globalDataContext, templateContext });
  const priority = evaluateTemplateValue({ valueToEvaluate: props.priority, globalDataContext, templateContext });
  const complexity = evaluateTemplateValue({ valueToEvaluate: props.complexity, globalDataContext, templateContext });
  const rawTags = evaluateTemplateValue({ valueToEvaluate: props.tags, globalDataContext, templateContext });
  const tags = Array.isArray(rawTags) ? rawTags.map(refId) : rawTags;
  const isUrgent = evaluateTemplateValue({ valueToEvaluate: props.isUrgent, globalDataContext, templateContext });
  const estimation = evaluateTemplateValue({ valueToEvaluate: props.estimation, globalDataContext, templateContext });
  const assignee = refId(evaluateTemplateValue({ valueToEvaluate: props.assignee, globalDataContext, templateContext }));

  const prevRef = useRef(null);

  useEffect(() => {
    if (!taskId) return;

    const current = { priority, complexity, tags, isUrgent, estimation, assignee };
    const currentKey = JSON.stringify(current);

    if (prevRef.current !== null && prevRef.current !== currentKey) {
      document.dispatchEvent(new CustomEvent('kanbanCardUpdated', {
        detail: {
          taskId,
          updates: {
            rating: priority,
            complexity,
            tags,
            isUrgent,
            estimation,
            assignee,
          },
        },
      }));
    }
    prevRef.current = currentKey;
  }, [taskId, priority, complexity, tags, isUrgent, estimation, assignee]);

  return null;
};

// ── KanbanReturnTypeLabel ────────────────────────────────────────────────────
export const KanbanReturnTypeLabel = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const value = evaluateTemplateValue({ valueToEvaluate: props.value, globalDataContext, templateContext });
  const returnTypeOptions = evaluateTemplateValue({ valueToEvaluate: props.returnTypeOptions, globalDataContext, templateContext }) || [];

  if (!value) return null;

  const opt = returnTypeOptions.find(o => String(o.value) === String(value));
  const label = opt ? opt.label : String(value);

  return <p className="text-xs text-slate-500 mb-2">{label}</p>;
};
